import React, { useState, useEffect } from 'react'
import {
  MagnifyingGlassIcon,
  TruckIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserIcon,
  MapPinIcon,
  MapIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../components/LoadingSpinner'
import RouteMapModal from '../../components/ui/RouteMapModal'

interface Booking {
  id: string
  status: string
  scheduledDateTime: string
  serviceType: string
  location: string
  originAddress?: string
  destinationAddress?: string
  coordinates?: {
    origin: { lat: number; lng: number }
    destination: { lat: number; lng: number }
  }
  pricing: {
    total: number
    currency: string
    basePrice: number
    breakdown: string
    emergencySurge?: number
  }
  notes?: string
  onSiteContact?: {
    name: string
    phone: string
    shareWithProvider: boolean
  }
  funeralCase?: {
    id: string
    caseNumber: string
    deceasedName: string
    status: string
    urgencyLevel?: string
    createdAt: string
    primaryContact: string
    primaryContactPhone: string
    user?: {
      firstName: string
      lastName: string
      phone: string
    }
  }
  // Burial plot specific fields
  burialSize?: string
  burialArea?: string
  preparationDate?: string
  urgencyLevel?: string
  specialRequests?: string
  plot?: {
    id: string
    name: string
    plotNumber?: string
    section?: string
    row?: string
    plotSize?: string
    dimensions?: {
      depth: number
      width: number
      length: number
    }
  }
  createdAt: string
  updatedAt: string
  actions?: string[] // Available actions from backend
}

function ProviderBookingsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [bookings, setBookings] = useState<Booking[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [sortBy, setSortBy] = useState<'createdAt' | 'scheduledDateTime'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedBookingForMap, setSelectedBookingForMap] = useState<Booking | null>(null)
  const limit = 10

  const fetchBookings = async () => {
    setIsLoading(true)
    try {
      const token = localStorage.getItem('token')
      const apiUrl = `${import.meta.env.VITE_API_URL}/api/provider-dashboard/bookings?page=${currentPage}&limit=${limit}&status=${statusFilter}&sortBy=${sortBy}&sortOrder=${sortOrder}&search=${searchQuery}`

      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch bookings: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      const bookingsList = result.bookings || []
      setBookings(bookingsList)
      setTotal(result.pagination?.total || 0)
      setTotalPages(result.pagination?.pages || 1)
    } catch (error) {
      console.error('Failed to fetch provider bookings:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateBookingStatus = async (bookingId: string, status: string) => {
    try {
      const token = localStorage.getItem('token')

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/provider-dashboard/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      })

      if (!response.ok) {
        throw new Error(`Failed to update booking status: ${response.status} ${response.statusText}`)
      }

      // Refresh bookings list to show updated status
      fetchBookings()
    } catch (error) {
      console.error('Failed to update booking status:', error)
      // Show error to user
      alert('Failed to update booking status. Please try again.')
    }
  }

  const acceptBooking = async (bookingId: string) => {
    try {
      const token = localStorage.getItem('token')

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/provider-dashboard/bookings/${bookingId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to accept booking: ${response.status} ${response.statusText}`)
      }

      // Refresh bookings list to show updated status
      fetchBookings()
    } catch (error) {
      console.error('Failed to accept booking:', error)
      alert('Failed to accept booking. Please try again.')
    }
  }

  const startTrip = async (bookingId: string) => {
    try {
      const token = localStorage.getItem('token')

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/provider-dashboard/bookings/${bookingId}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to start trip: ${response.status} ${response.statusText}`)
      }

      // Refresh bookings list to show updated status
      fetchBookings()
    } catch (error) {
      console.error('Failed to start trip:', error)
      alert('Failed to start trip. Please try again.')
    }
  }

  const completeTrip = async (bookingId: string) => {
    try {
      const token = localStorage.getItem('token')

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/provider-dashboard/bookings/${bookingId}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })

      if (!response.ok) {
        throw new Error(`Failed to complete trip: ${response.status} ${response.statusText}`)
      }

      // Refresh bookings list to show updated status
      fetchBookings()
    } catch (error) {
      console.error('Failed to complete trip:', error)
      alert('Failed to complete trip. Please try again.')
    }
  }

  useEffect(() => {
    fetchBookings()
  }, [currentPage, statusFilter, sortBy, sortOrder, searchQuery])

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />
      case 'confirmed':
        return <CheckCircleIcon className="w-5 h-5 text-blue-500" />
      case 'in_progress':
        return <TruckIcon className="w-5 h-5 text-orange-500" />
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case 'cancelled':
        return <XCircleIcon className="w-5 h-5 text-red-500" />
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'confirmed':
        return 'bg-blue-100 text-blue-800'
      case 'in_progress':
        return 'bg-orange-100 text-orange-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusText = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const getServiceTypeText = (serviceType: string) => {
    const serviceTypeMap: { [key: string]: string } = {
      'AMBULANCE_TRANSPORT': 'Ambulance Transport',
      'GHUSL_SERVICE': 'Ghusl (Ritual Washing)',
      'KAFAN_SERVICE': 'Kafan (Shrouding)',
      'JANAZAH_LEAD': 'Janazah Prayer Lead',
      'BURIAL_PLOT': 'Burial Plot',
      'CATERING': 'Catering Services',
      'FLOWERS': 'Flowers & Decorations',
      'TENT_RENTAL': 'Tent & Equipment Rental',
      'PRINTING': 'Printing Services',
      'ISLAMIC_SOUVENIRS': 'Islamic Souvenirs',
      'PHOTOGRAPHY': 'Photography Services'
    }
    return serviceTypeMap[serviceType] || serviceType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const isBurialPlotBooking = (booking: Booking) => {
    return booking.serviceType === 'BURIAL_PLOT'
  }

  const isAmbulanceBooking = (booking: Booking) => {
    return booking.serviceType === 'AMBULANCE_TRANSPORT'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">Bookings</h1>
            <p className="mt-2 text-sm text-gray-700">
              A list of all your bookings including their status, customer details, and scheduling information.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700">
              Search
            </label>
            <div className="relative mt-1 rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="search"
                id="search"
                className="block w-full rounded-md border-gray-300 pl-10 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="Search bookings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              id="status"
              name="status"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div>
            <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700">
              Sort By
            </label>
            <select
              id="sortBy"
              name="sortBy"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'createdAt' | 'scheduledDateTime')}
            >
              <option value="createdAt">Created Date</option>
              <option value="scheduledDateTime">Scheduled Date</option>
            </select>
          </div>

          <div>
            <label htmlFor="sortOrder" className="block text-sm font-medium text-gray-700">
              Order
            </label>
            <select
              id="sortOrder"
              name="sortOrder"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bookings List */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <LoadingSpinner />
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-12">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings</h3>
            <p className="mt-1 text-sm text-gray-500">
              {statusFilter || searchQuery ? 'Try adjusting your filters' : 'You haven\'t received any bookings yet'}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {bookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-lg shadow">
              <div className="p-6">
                {/* Booking Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {booking.funeralCase?.deceasedName ||
                         (isBurialPlotBooking(booking) ? 'Burial Plot Booking' : 'Ambulance Booking')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Booking ID: {booking.id}
                      </p>
                      <p className="text-sm text-gray-500">
                        {getServiceTypeText(booking.serviceType)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(booking.status)}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      getStatusColor(booking.status)
                    }`}>
                      {getStatusText(booking.status)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Service Information */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Service Information Card - Dynamic based on service type */}
                    {isBurialPlotBooking(booking) ? (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center mb-3">
                          <BuildingOfficeIcon className="h-5 w-5 text-gray-500 mr-2" />
                          <h4 className="text-base font-semibold text-gray-900">Burial Plot Information</h4>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                            <p className="text-gray-900">{getServiceTypeText(booking.serviceType)}</p>
                          </div>

                          {/* Plot Details - Enhanced Display */}
                          {(booking.plot || booking.burialArea) && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <MapPinIcon className="h-4 w-4 mr-1 text-green-600" />
                                Plot Details
                              </label>
                              <div className="bg-green-50 rounded-md p-3 border border-green-200">
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {booking.plot?.plotNumber && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Plot Number:</span>
                                      <span className="font-medium text-gray-900">{booking.plot.plotNumber}</span>
                                    </div>
                                  )}
                                  {booking.plot?.section && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Section:</span>
                                      <span className="font-medium text-gray-900">{booking.plot.section}</span>
                                    </div>
                                  )}
                                  {booking.plot?.row && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Row:</span>
                                      <span className="font-medium text-gray-900">{booking.plot.row}</span>
                                    </div>
                                  )}
                                  {booking.plot?.plotSize && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Size:</span>
                                      <span className="font-medium text-gray-900">{booking.plot.plotSize}</span>
                                    </div>
                                  )}
                                  {booking.plot?.dimensions && (
                                    <div className="flex justify-between col-span-2">
                                      <span className="text-gray-600">Dimensions:</span>
                                      <span className="font-medium text-gray-900">
                                        {booking.plot.dimensions.length}m × {booking.plot.dimensions.width}m × {booking.plot.dimensions.depth}m
                                      </span>
                                    </div>
                                  )}
                                  {/* Fallback to burial area if plot details are not available */}
                                  {!booking.plot && booking.burialArea && (
                                    <div className="flex justify-between col-span-2">
                                      <span className="text-gray-600">Cemetery Area:</span>
                                      <span className="font-medium text-gray-900">{booking.burialArea}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Burial Information */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Burial Information</label>
                            <div className="text-sm text-gray-900 space-y-1">
                              {booking.burialSize && <div><strong>Burial Size:</strong> {booking.burialSize}</div>}
                              {booking.urgencyLevel && <div><strong>Urgency Level:</strong> {booking.urgencyLevel}</div>}
                            </div>
                          </div>

                          {/* Scheduled Date */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date & Time</label>
                            <div className="flex items-center space-x-2">
                              <CalendarIcon className="h-4 w-4 text-gray-400" />
                              <p className="text-gray-900 text-sm">
                                {formatDateTime(booking.scheduledDateTime)}
                              </p>
                            </div>
                          </div>

                          {/* Preparation Date */}
                          {booking.preparationDate && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Preparation Date</label>
                              <div className="flex items-center space-x-2">
                                <CalendarIcon className="h-4 w-4 text-gray-400" />
                                <p className="text-gray-900 text-sm">
                                  {formatDateTime(booking.preparationDate)}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Special Requests */}
                          {booking.specialRequests && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
                              <p className="text-gray-900 text-sm">{booking.specialRequests}</p>
                            </div>
                          )}

                          {/* Service Details */}
                          {booking.notes && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Service Details</label>
                              <p className="text-gray-900 text-sm">{booking.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center mb-3">
                          <TruckIcon className="h-5 w-5 text-gray-500 mr-2" />
                          <h4 className="text-base font-semibold text-gray-900">Trip Information</h4>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                            <p className="text-gray-900">{getServiceTypeText(booking.serviceType)}</p>
                          </div>
                          {booking.notes && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Service Details</label>
                              <p className="text-gray-900">{booking.notes}</p>
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
                            <div className="flex items-start space-x-2">
                              <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5" />
                              <p className="text-gray-900 text-sm">
                                {booking.originAddress || booking.location}
                              </p>
                            </div>
                          </div>
                          {booking.destinationAddress && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Destination Location</label>
                              <div className="flex items-start space-x-2">
                                <MapPinIcon className="h-4 w-4 text-red-500 mt-0.5" />
                                <p className="text-gray-900 text-sm">{booking.destinationAddress}</p>
                              </div>
                            </div>
                          )}
                          {booking.coordinates?.origin && (
                            <div className="text-xs text-gray-500 space-y-1">
                              <div>Pickup: {booking.coordinates.origin.lat}, {booking.coordinates.origin.lng}</div>
                              {booking.coordinates.destination && (
                                <div>Destination: {booking.coordinates.destination.lat}, {booking.coordinates.destination.lng}</div>
                              )}
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date & Time</label>
                            <div className="flex items-center space-x-2">
                              <ClockIcon className="h-4 w-4 text-gray-400" />
                              <p className="text-gray-900 text-sm">
                                {formatDateTime(booking.scheduledDateTime)}
                              </p>
                            </div>
                          </div>

                          {/* Open in Map Button */}
                          {booking.coordinates && booking.coordinates.destination && (
                            <div>
                              <button
                                onClick={() => setSelectedBookingForMap(booking)}
                                className="flex items-center justify-center w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              >
                                <MapIcon className="h-4 w-4 mr-2" />
                                Open in Map
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Family Information Card */}
                    {booking.funeralCase && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center mb-3">
                          <UserIcon className="h-5 w-5 text-gray-500 mr-2" />
                          <h4 className="text-base font-semibold text-gray-900">Family Information</h4>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Deceased Name</label>
                            <p className="text-gray-900">{booking.funeralCase.deceasedName}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Case Number</label>
                            <p className="text-gray-900">{booking.funeralCase.caseNumber}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                            <p className="text-gray-900">
                              {booking.onSiteContact?.shareWithProvider
                                ? booking.onSiteContact.name
                                : booking.funeralCase.primaryContact}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                            <p className="text-gray-900">
                              {booking.onSiteContact?.shareWithProvider
                                ? booking.onSiteContact.phone
                                : booking.funeralCase.primaryContactPhone}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Status & Payment */}
                  <div className="space-y-6">
                    {/* Booking Status Card */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center mb-3">
                        <ClockIcon className="h-5 w-5 text-gray-500 mr-2" />
                        <h4 className="text-base font-semibold text-gray-900">Booking Status</h4>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-600">Current Status</p>
                          <div className="flex items-center space-x-2 mt-1">
                            {getStatusIcon(booking.status)}
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(booking.status)}`}>
                              {getStatusText(booking.status)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Created</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatDateTime(booking.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Information Card */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center mb-3">
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-500 mr-2" />
                        <h4 className="text-base font-semibold text-gray-900">Payment Information</h4>
                      </div>
                      <div className="space-y-3">
                        {booking.funeralCase && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Case Status</label>
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {booking.funeralCase.status.replace(/_/g, ' ')}
                            </span>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                          <p className="text-lg font-semibold text-gray-900">
                            {booking.pricing.currency} {booking.pricing.total.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Base Price</label>
                          <p className="text-sm text-gray-900">
                            {booking.pricing.currency} {booking.pricing.basePrice.toLocaleString()}
                          </p>
                        </div>
                        {booking.pricing.breakdown && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Price Breakdown</label>
                            <p className="text-xs text-gray-600">{booking.pricing.breakdown}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions Card */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="text-base font-semibold text-gray-900 mb-3">Actions</h4>
                      <div className="space-y-3">
                        {/* Use backend actions if available, otherwise use legacy logic */}
                        {booking.actions && booking.actions.length > 0 ? (
                          <>
                            {booking.actions.includes('accept') && (
                              <button
                                onClick={() => acceptBooking(booking.id)}
                                className="block w-full text-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                Accept Booking
                              </button>
                            )}

                            {booking.actions.includes('reject') && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'CANCELLED')}
                                className="block w-full text-center bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                Reject Booking
                              </button>
                            )}

                            {booking.actions.includes('start_trip') && (
                              <button
                                onClick={() => startTrip(booking.id)}
                                className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                Start Trip
                              </button>
                            )}

                            {booking.actions.includes('complete_trip') && (
                              <button
                                onClick={() => completeTrip(booking.id)}
                                className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                Complete Trip
                              </button>
                            )}

                            {booking.actions.includes('view_details') && (
                              <button
                                onClick={() => {
                                  // You can navigate to details page or show a modal
                                  console.log('View details for booking:', booking.id)
                                }}
                                className="block w-full text-center bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                View Details
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            {/* Legacy logic for backward compatibility */}
                            {booking.status === 'PENDING' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'CONFIRMED')}
                                className="block w-full text-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                Confirm Booking
                              </button>
                            )}

                            {booking.status === 'CONFIRMED' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'IN_PROGRESS')}
                                className="block w-full text-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                {isBurialPlotBooking(booking) ? 'Start Preparation' : 'Start Trip'}
                              </button>
                            )}

                            {booking.status === 'IN_PROGRESS' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'COMPLETED')}
                                className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                {isBurialPlotBooking(booking) ? 'Complete Burial' : 'Complete Trip'}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of{' '}
              {total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Route Map Modal */}
      {selectedBookingForMap && selectedBookingForMap.coordinates && selectedBookingForMap.coordinates.destination && (
        <RouteMapModal
          isOpen={true}
          onClose={() => setSelectedBookingForMap(null)}
          origin={selectedBookingForMap.coordinates.origin}
          destination={selectedBookingForMap.coordinates.destination}
          originAddress={selectedBookingForMap.originAddress || selectedBookingForMap.location}
          destinationAddress={selectedBookingForMap.destinationAddress}
        />
      )}
    </div>
  )
}

export default ProviderBookingsPage